import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useAuthorityLevel from '@/hooks/useAuthorityLevel';
import usePermission from '@/hooks/usePermission';
import { Loader } from '@/components/custom/loader';
import { EmptyState } from '@/components/custom/empty-state';
import { TiaBanner } from '@/components/custom/tia-banner';
import { DashboardContent } from '@/layouts/dashboard';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { ProjectsContext } from '../provider';
import { ProjectForm } from '../ProjectForm';
import { ProjectsPlayfulCard } from './ProjectsCard';
import tiaInstrucoes from '@/assets/images/tia/TIA-INSTRUCOES.png';

export default function ProjectsPlayful({ uri }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const authorityLevel = useAuthorityLevel();
  const [show, setShow] = useState(false);

  const { loading, projects, getProjects } = useContext(ProjectsContext);

  useEffect(() => {
    getProjects();
  }, []);

  useEffect(() => {
    if (authorityLevel !== 'student') {
      navigate('/projetos/grupos');
    }
  }, [authorityLevel]);

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: uri,
        name: 'Projetos',
        isActive: true
      }
    ]
  };

  if (!usePermission('Visualizar lista de projetos', 'Projetos')) {
    return null;
  }

  return (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <TiaBanner
        className="mb-6 mt-16"
        image={tiaInstrucoes}
        text="Aqui estão os seus projetos! Acompanhe o andamento, envie novas versões e confira as avaliações. Vamos trabalhar em equipe? 🤝"
      />

      {usePermission('Criar projetos', 'Projetos') && (
        <div className="mb-6 flex justify-end">
          <button
            type="button"
            onClick={() => setShow(true)}
            className="rounded-full px-5 py-2.5 text-sm font-extrabold text-white transition-transform duration-100 active:translate-y-px"
            style={{ background: 'var(--gradient-btn-continue)', boxShadow: '0 3px 0 var(--color-mission-dark)' }}
          >
            {t('projects.newProjects')}
          </button>
        </div>
      )}

      {loading && <Loader />}

      {!loading && projects.length === 0 && (
        <EmptyState
          type="data"
          variant="plain"
          title={t('emptyState.inAnyProjectTitle')}
          text={t('emptyState.inAnyProjectText')}
        />
      )}

      {!loading && projects.length > 0 && (
        <div className="grid animate-fade-in grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {projects.map(p => (
            <ProjectsPlayfulCard
              data={p}
              key={p.id}
            />
          ))}
        </div>
      )}

      <Dialog
        open={show}
        onOpenChange={setShow}
      >
        <DialogContent>
          <DialogTitle className="sr-only">Novo Projeto</DialogTitle>
          <ProjectForm
            labelForm={'Criar novo projeto'}
            labelFormButton={t('button.saveContinue')}
          />
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
